import { MotifTruncate, MotifIcon, MotifTooltip } from "@ey-xd/motif-react";
import {
  IconoirNavArrowUp,
  IconoirNavArrowDown,
  IconoirXmarkCircle,
  IconoirCheckCircle,
  IconoirRefresh,
} from "@ey-xd/motif-icon";
import {
  getFormattedDate,
  getSyncFormattedDate,
  getTotalOrderAmount,
  onViewOrderItems,
  getArrowDirection,
} from "./helper";

interface Order {
  OrderParentId: number;
  Description: string;
  CreatedDtm: string;
  OpportunityId?: string;
  StatusCd: string;
  UpdatedDtm: string;
  CreatedUserEmail: string;
  CreatedUserName: string;
  CRMSyncDtm: null | string;
  CRMSyncFailureStatus: null | number;
  IsCRMSyncRetryAllowed: number;
  IsCRMSyncRequired: boolean;
}

interface OrderColumn {
  key: string;
  label: string;
  width: string;
  render: (order: Order) => React.ReactNode;
}

export const getEstimatedOrderColumns = (
  setEstimatedOrderDetails: React.Dispatch<React.SetStateAction<any>>,
  estimatedOrderDetails: any,
  getOrderAction?: (
    order: any,
    disabled: boolean,
    selectedTab: number,
  ) => React.ReactNode,
  emailId?: string,
  handleSyncOrder?: (orderId: number, status?: string) => void,
): OrderColumn[] => [
  {
    key: "orderName",
    label: "Estimate Name/ID",
    width: "28%",
    render: (order: Order) => (
      <div className="sku-name-responsive">
        <MotifTruncate maxLines={1} className="sku-truncate-responsive">
          {order.Description}
        </MotifTruncate>
        <div className="order-id">{order.OrderParentId}</div>
      </div>
    ),
  },
  {
    key: "createdDtm",
    label: "Date created",
    width: "14%",
    render: (order: Order) => (
      <div className="order-date">{getFormattedDate(order.CreatedDtm)}</div>
    ),
  },
  {
    key: "opportunityCode",
    label: "Opportunity Code",
    width: "16%",
    render: (order: Order) => (
      <div className="order-o-code">
        {order.OpportunityId ? (
          <>
            <div className="order-o-code-items">
              <div>{order.OpportunityId}</div>
              {order.IsCRMSyncRequired && (
                <div>
                  {order.CRMSyncFailureStatus != null &&
                    order.CRMSyncFailureStatus == 0 && (
                      <MotifTooltip
                        placement="auto"
                        allowHover
                        allowClick={false}
                        hideCloseButton
                        trigger={
                          <MotifIcon
                            iconFunction={IconoirCheckCircle}
                            size="15"
                            className="success-icon"
                          />
                        }
                      >
                        <div className="crm-sync-tooltip">
                          <div className="crm-sync-title">
                            Last saved to CRM
                          </div>
                          <div className="crm-sync-body">
                            {getSyncFormattedDate(order.CRMSyncDtm || "")} (UTC)
                          </div>
                        </div>
                      </MotifTooltip>
                    )}
                  {order.CRMSyncFailureStatus != null &&
                    order.CRMSyncFailureStatus >= 1 && (
                      <MotifTooltip
                        placement="auto"
                        allowHover
                        allowClick={false}
                        hideCloseButton
                        trigger={
                          <MotifIcon
                            iconFunction={IconoirXmarkCircle}
                            size="15"
                            className="warning-icon"
                          />
                        }
                      >
                        <div className="crm-sync-tooltip">
                          <div className="crm-sync-title">CRM save failed</div>
                          {order.CRMSyncDtm && (
                            <div className="crm-sync-body">
                              Last successful saved on <br />
                              {getSyncFormattedDate(
                                order.CRMSyncDtm || "",
                              )}{" "}
                              (UTC)
                            </div>
                          )}
                        </div>
                      </MotifTooltip>
                    )}
                </div>
              )}
            </div>
            {order.IsCRMSyncRequired && (
              <div className="retry-sync-container">
                {order.IsCRMSyncRetryAllowed == 1 && handleSyncOrder && (
                  <div
                    className="retry-sync"
                    onClick={() =>
                      handleSyncOrder(order.OrderParentId, "estimate")
                    }
                  >
                    <div>Retry Sync</div>
                    <div>
                      <MotifIcon
                        iconFunction={IconoirRefresh}
                        size="15"
                        className="retry-icon"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          "----"
        )}
      </div>
    ),
  },
  {
    key: "createdBy",
    label: "Created By",
    width: "16%",
    render: (order: Order) => {
      return (
        <div className="order-date">
          {emailId &&
          order.CreatedUserEmail.toLocaleLowerCase() !==
            emailId.toLocaleLowerCase() ? (
            <MotifTruncate maxLines={1} maxWidth={150}>
              {order.CreatedUserName || ""}
            </MotifTruncate>
          ) : (
            "Me"
          )}
        </div>
      );
    },
  },
  {
    key: "totalCost",
    label: "Est. Cost",
    width: "14%",
    render: (order: Order) => (
      <div className="order-cost">{getTotalOrderAmount(order)}</div>
    ),
  },
  {
    key: "viewOrderItems",
    label: "View Order Items",
    width: "18%",
    render: (order: Order) => {
      const isExpanded =
        getArrowDirection(order.OrderParentId, estimatedOrderDetails) === "up";
      return (
        <div className="view-order">
          <div
            className="view-order-items"
            onClick={() =>
              setEstimatedOrderDetails(
                onViewOrderItems(order.OrderParentId, estimatedOrderDetails),
              )
            }
            style={{ cursor: "pointer" }}
          >
            <div>{isExpanded ? "Close Order Items" : "View Order Items"}</div>
            <div>
              {isExpanded ? (
                <MotifIcon iconFunction={IconoirNavArrowUp} size="20" />
              ) : (
                <MotifIcon iconFunction={IconoirNavArrowDown} size="20" />
              )}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    key: "action",
    label: "Action",
    width: "12%",
    render: (order: Order) => {
      return (
        <div className={`order-action`}>
          {getOrderAction
            ? getOrderAction(
                order,
                emailId &&
                  order.CreatedUserEmail.toLocaleLowerCase() !==
                    emailId.toLocaleLowerCase()
                  ? true
                  : false,
                2,
              )
            : null}
        </div>
      );
    },
  },
];

export const getDraftOrderColumns = (
  setDraftOrderDetails: React.Dispatch<React.SetStateAction<any>>,
  draftOrderDetails: any,
  getOrderAction?: (order: any) => React.ReactNode,
): OrderColumn[] => [
  {
    key: "orderName",
    label: "Draft Name/ID",
    width: "30%",
    render: (order: Order) => (
      <div className="sku-name-responsive">
        <MotifTruncate maxLines={1} className="sku-truncate-responsive">
          {order.Description}
        </MotifTruncate>
        <div className="order-id">{order.OrderParentId}</div>
      </div>
    ),
  },
  {
    key: "createdDtm",
    label: "Date created",
    width: "13%",
    render: (order: Order) => (
      <div className="order-date">{getFormattedDate(order.CreatedDtm)}</div>
    ),
  },
  {
    key: "UpdatedDtm",
    label: "Last Modified On",
    width: "14%",
    render: (order: Order) => (
      <div className="order-date">{getFormattedDate(order.UpdatedDtm)}</div>
    ),
  },
  {
    key: "totalCost",
    label: "Est. Cost",
    width: "11%",
    render: (order: Order) => (
      <div className="order-total">{getTotalOrderAmount(order)}</div>
    ),
  },
  {
    key: "viewOrderItems",
    label: "View Draft Items",
    width: "17%",
    render: (order: Order) => {
      const isExpanded =
        getArrowDirection(order.OrderParentId, draftOrderDetails) === "up";
      return (
        <div className="view-order">
          <div
            className="view-order-items"
            onClick={() =>
              setDraftOrderDetails(
                onViewOrderItems(order.OrderParentId, draftOrderDetails),
              )
            }
            style={{ cursor: "pointer" }}
          >
            <div>{isExpanded ? "Close Draft Items" : "View Draft Items"}</div>
            <div>
              {isExpanded ? (
                <MotifIcon iconFunction={IconoirNavArrowUp} size="20" />
              ) : (
                <MotifIcon iconFunction={IconoirNavArrowDown} size="20" />
              )}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    key: "action",
    label: "Action",
    width: "15%",
    render: (order: any) => (
      <div className="order-action">
        {getOrderAction ? getOrderAction(order) : null}
      </div>
    ),
  },
];
